import { Box, Stack } from '@mui/material';
import { useEffect, useReducer, useState } from 'react';
import { useHistory } from 'react-router';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import { ApplicationAccessType, CommunicationType } from 'domains/content/types';
import useCreateVoiceContent from 'hooks/mutations/useCreateVoiceContent';
import useUpdateVoiceContent from 'hooks/mutations/useUpdateVoiceContent';
import useVoiceContentById from 'hooks/queries/useVoiceContentById';
import useQueryParams from 'hooks/useQueryParams';
import { URLPaths } from 'models/enums';
import { voiceContentReducer, voiceContentInitialState } from './voiceCallReducer';
import { setApplicationsAccess, setCommunicationType, setName, setVoiceContentState } from './voiceCallActions';
import VoiceCallBuilder from './VoiceCallBuilder';
import VoiceBuilderPageHeader from './VoiceBuilderPageHeader';
import VoicemailFallbackBuilder from './VoicemailFallbackBuilder';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

const ContentVoiceBuilderPage = () => {
    const [state, dispatch] = useReducer(voiceContentReducer, voiceContentInitialState);
    const { contentDraft: hasContentDraft } = useFlags();
    const [isVoicemailActive, setIsVoicemailActive] = useState(false);

    const history = useHistory();

    const query = useQueryParams();
    const voiceContentId = query.get('id');
    const parentFolderId = query.get('parentFolderId');
    const isCopy = query.get('isCopy') === 'true';
    // Status from URL is prioritized over API response to handle race condition when converting
    // published content to draft - the API cache might return stale "published" status before refetch completes
    const statusFromUrl = query.get('status') as ContentStatus | null;

    const { data, isSuccess, isInitialLoading: isLoading } = useVoiceContentById({
        voiceContentId,
        reactQueryOptions: {
            enabled: !!voiceContentId,
        },
    });

    const navigateToContent = () => {
        const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
        history.push(contentPath);
    };

    const {
        mutate: createVoiceContent,
        isLoading: isCreateLoading,
        isError: isCreateError,
        error: createError,
    } = useCreateVoiceContent({
        onSuccess: navigateToContent,
    });

    const {
        mutate: updateVoiceContent,
        isLoading: isUpdateLoading,
        isError: isUpdateError,
        error: updateError,
    } = useUpdateVoiceContent(voiceContentId, {
        onSuccess: navigateToContent,
    });

    useEffect(() => {
        if (!voiceContentId) {
            dispatch(setName('Untitled'));
            dispatch(setApplicationsAccess([ApplicationAccessType.OUTREACH]));
            dispatch(setCommunicationType(CommunicationType.COMMERCIAL));
        }
    }, [voiceContentId]);

    useEffect(() => {
        if (isSuccess && data) {
            dispatch(
                setVoiceContentState({
                    name: isCopy ? `Copy of ${data.name}` : data.name,
                    voiceJson: data.voiceJson,
                    communicationType: data.communicationType,
                    applicationsAccess: data.applicationsAccess.split(',') as ApplicationAccessType[],
                })
            );
        }
    }, [isSuccess, data, isCopy]);

    const handleSave = (status?: ContentStatus) => {
        const mutateInput = {
            name: state.name?.length === 0 ? 'Untitled' : state.name,
            voiceJson: state.voiceJson,
            communicationType: state.communicationType,
            applicationsAccess: state.applicationsAccess.join(','),
            ...(parentFolderId ? { parentFolderId: Number(parentFolderId) } : {}),
            ...(status && { status }),
        };

        voiceContentId && !isCopy
            ? updateVoiceContent({ id: voiceContentId, ...mutateInput })
            : createVoiceContent(mutateInput);
    };

    const isError = isCreateError || isUpdateError;

    return (
        <Stack rowGap={4}>
            {isError && <ErrorSnackbar errorMessage={createError?.message || updateError?.message} />}

            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    disabled={isLoading}
                    dataTestId="voice-content-name-input"
                    name={state.name}
                    onChange={(e) => dispatch(setName(e.target.value))}
                    withClickAway
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                voiceContentId && !isCopy
                                    ? statusFromUrl || (data as any)?.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>
            <Stack height="100%" mb={4}>
                <VoiceBuilderPageHeader
                    applicationsAccess={state.applicationsAccess}
                    communicationType={state.communicationType}
                    dispatchVoiceContentAction={dispatch}
                    handleSave={handleSave}
                    isSaving={isCreateLoading || isUpdateLoading}
                    isVoicemailActive={isVoicemailActive}
                    setIsVoicemailActive={setIsVoicemailActive}
                    voiceContentState={state}
                />
                {isVoicemailActive ? (
                    <VoicemailFallbackBuilder
                        voiceContentState={state}
                        dispatchVoiceContentAction={dispatch}
                        isContentLoading={isLoading}
                    />
                ) : (
                    <VoiceCallBuilder
                        voiceContentState={state}
                        dispatchVoiceContentAction={dispatch}
                        isMessageLoading={isLoading}
                    />
                )}
            </Stack>
        </Stack>
    );
};

export default ContentVoiceBuilderPage;
